import inquirer, {
    CheckboxQuestion,
    ChoiceOptions,
    ConfirmQuestion,
    DistinctQuestion,
    EditorQuestion,
    InputQuestion,
    ListQuestion,
    NumberQuestion,
    PasswordQuestion,
    Question,
    RawListQuestion,
} from "inquirer";

export type OmittedQuestionProperties = "name" | "type" | "message" | "choices";

export type StandardPromptFunction<
    TQuestion extends Question,
    TDefaultValue
> = <TValue = TDefaultValue>(
    message: string,
    options?: Omit<TQuestion, OmittedQuestionProperties>,
) => Promise<TValue>;

export type PromptFunctionWithChoices<
    TQuestion extends Question,
    TDefaultValue
> = <TValue = TDefaultValue, TAnswers = {}>(
    message: string,
    choices: (ChoiceOption<TValue, TAnswers> | string)[],
    options?: Omit<TQuestion, OmittedQuestionProperties>,
) => Promise<TValue>;

export interface PromptApi {
    confirm: StandardPromptFunction<ConfirmQuestion, boolean>;
    input: StandardPromptFunction<InputQuestion, boolean>;
    number: StandardPromptFunction<NumberQuestion, boolean>;
    password: StandardPromptFunction<PasswordQuestion, boolean>;
    editor: StandardPromptFunction<EditorQuestion, boolean>;

    list: PromptFunctionWithChoices<ListQuestion, string>;
    rawlist: PromptFunctionWithChoices<ListQuestion, string>;

    checkbox<TValue = string, TAnswers = {}>(
        message: string,
        choices: (ChoiceOption<TValue, TAnswers> | string)[],
        options?: Omit<CheckboxQuestion, OmittedQuestionProperties> & {
            min?: number;
            max?: number;
        },
    ): Promise<TValue[]>;
}

export type ChoiceOption<TValue = string, TAnswers = {}> = ChoiceOptions<
    TAnswers
> & { value: TValue };

const mapStringToChoiceOption = <TAnswers = {}>(
    value: string,
): ChoiceOption<string, TAnswers> => ({
    type: "choice",
    name: value.toString(),
    short: value.toString(),
    value,
});

const ensureChoiceOption = <TValue = string, TAnswers = {}>(
    option: TValue | ChoiceOptions<TAnswers>,
): ChoiceOption<TValue, TAnswers> =>
    typeof option === "string"
        ? mapStringToChoiceOption<TAnswers>(option)
        : ({
              type: "choice",
              name: (option as ChoiceOptions<TAnswers>).name,
              short: (option as ChoiceOptions<TAnswers>).name,
              value: (option as ChoiceOptions<TAnswers>).value,
          } as ChoiceOption<TValue, TAnswers>);

const words = {
    option: (n: number) => (n === 1 ? "option" : "options"),
};

const ask = async <TValue, TAnswers = {}>(
    message: string,
    question: Omit<DistinctQuestion, "name"> = {},
): Promise<TValue> =>
    (
        await inquirer.prompt({
            ...question,
            message,
            name: "value",
        } as DistinctQuestion)
    )["value"];

const prompt: PromptApi = {
    confirm: (message, options) =>
        ask(message, { type: "confirm", ...options }),
    input: (message, options) => ask(message, { type: "input", ...options }),
    number: (message, options) => ask(message, { type: "number", ...options }),
    password: (message, options) =>
        ask(message, { type: "password", ...options }),
    editor: (message, options) => ask(message, { type: "editor", ...options }),
    list: (message, choices, options) =>
        ask(message, {
            type: "list",
            choices: choices.map(ensureChoiceOption) as ChoiceOptions[],
            ...options,
        } as ListQuestion),
    rawlist: (message, choices, options) =>
        ask(message, {
            type: "rawlist",
            choices: choices.map(ensureChoiceOption) as ChoiceOptions[],
            ...options,
        } as RawListQuestion),
    checkbox: (message, choices, opts = {}) => {
        const { min, max, ...options } = opts;

        return ask(message, {
            ...options,
            type: "checkbox",
            choices: choices.map(ensureChoiceOption) as ChoiceOptions[],
            validate: (v = [], answers) => {
                if (min && v.length < min) {
                    return `Select at least ${min} ${words.option(min)}.`;
                } else if (max && v.length > max) {
                    return `Select no more than ${min} ${words.option(max)}.`;
                }

                return !options.validate || options.validate(v, answers);
            },
        } as CheckboxQuestion);
    },
};

export default prompt;
